import { catalog } from '../core/catalog.js';
import { t } from '../core/i18n.js';
import { debounce, escapeHtml, normalizeText } from '../core/utils.js';
import { device } from '../core/device.js';
import { searchHistory } from '../core/search-history.js';
import { movieCard, seriesCard, channelCard, row, empty, enhance } from '../ui/render.js';
import { bindVoice } from '../core/voice.js';
import { notify } from '../core/notify.js';

/* Búsqueda global con historial: al escribir se sugieren búsquedas anteriores y títulos del
   catálogo; sin texto se muestran las búsquedas recientes (se pueden quitar una a una o todas). */

const SUGGEST_ICON = { history: 'history', vod: 'movie', series: 'tv', live: 'live_tv' };

export default {
    path: '/search/',
    nav: 'search',

    render() {
        return `<div class="page" data-name="search">
            <div class="page-content">
                <div class="page-head">
                    <div class="search-wrap">
                        <div class="searchbox"><span class="mi">search</span><input type="search" data-search placeholder="${t('search.placeholder')}" autocomplete="off" autocorrect="off" spellcheck="false" data-nav-default/><button class="searchbox-clear" data-search-clear hidden aria-label="${t('common.close')}"><span class="mi">close</span></button><button type="button" class="searchbox-mic" data-voice hidden aria-label="${t('voice.label')}"><span class="mi">mic</span></button></div>
                        <div class="search-suggest" data-suggest hidden></div>
                    </div>
                </div>
                <div data-results></div>
            </div>
        </div>`;
    },

    mount(page, route) {
        const root = page.el;
        const input = root.querySelector('[data-search]');
        const clear = root.querySelector('[data-search-clear]');
        bindVoice(root, { onError: (code) => notify.error(t(code === 'denied' ? 'voice.denied' : 'voice.unavailable')) });
        const results = root.querySelector('[data-results]');
        const suggest = root.querySelector('[data-suggest]');
        let last = '';
        let saveTimer = null;
        let alive = true;
        let scanning = false;
        let stopScan = false;
        let scanText = '';

        // Para buscar por actor o director hay que conocer los repartos: se ofrece leerlos de todo el catálogo (una vez).
        const scanBarHtml = () => {
            if (!scanning && catalog.castScanDone()) return '';
            if (scanning) return `<div class="search-scan" data-scan-bar><div class="spinner"></div><span data-scan-text>${escapeHtml(scanText)}</span><button type="button" class="btn btn-sm btn-ghost" data-cast-stop>${t('actor.stop')}</button></div>`;
            return `<div class="search-scan" data-scan-bar><span class="mi">person_search</span><span>${t('search.castHint')}</span><button type="button" class="btn btn-sm" data-cast-scan>${t('actor.scan')}</button></div>`;
        };

        const startScan = async () => {
            if (scanning || !catalog.loaded) return;
            scanning = true;
            stopScan = false;
            scanText = t('actor.scanning', { done: 0, total: catalog.visible('vod').length + catalog.visible('series').length });
            const bar = results.querySelector('[data-scan-bar]');
            if (bar) bar.outerHTML = scanBarHtml();
            let lastRefresh = 0;
            const complete = await catalog.scanDetails({
                shouldStop: () => stopScan || !alive,
                onProgress: (done, total) => {
                    scanText = t('actor.scanning', { done, total });
                    const el = results.querySelector('[data-scan-text]');
                    if (el) el.textContent = scanText;
                    if (done - lastRefresh >= 60 && input.value.trim().length >= 2) { lastRefresh = done; last = ''; run(); }
                }
            });
            scanning = false;
            if (!alive) return;
            if (complete) notify.toast(t('actor.scanDone'), { icon: 'check' });
            last = '';
            run();
        };

        const recentHtml = () => {
            const list = searchHistory.list();
            if (!list.length) return empty({ icon: 'search', title: t('search.title'), text: t('search.hint') });
            return `<section class="section search-recent">
                <div class="section-head"><h2 class="section-title">${t('search.recent')}</h2><button class="btn btn-ghost btn-sm" data-history-clear><span class="mi">delete_sweep</span>${t('search.clearHistory')}</button></div>
                <div class="search-recent-list" data-nav-row>${list.map((q) => `<span class="chip search-chip"><button class="search-chip-text" data-hq="${escapeHtml(q)}"><span class="mi">history</span>${escapeHtml(q)}</button><button class="search-chip-x" data-hx="${escapeHtml(q)}" aria-label="${t('common.delete')}" ${device.isTV ? 'data-nav-skip tabindex="-1"' : ''}><span class="mi">close</span></button></span>`).join('')}</div>
            </section>`;
        };

        const showRecent = () => {
            results.innerHTML = recentHtml();
            enhance(results);
        };

        const hideSuggest = () => {
            suggest.hidden = true;
            suggest.innerHTML = '';
        };

        const renderSuggest = async () => {
            const q = input.value.trim();
            if (!q) return hideSuggest();
            const items = searchHistory.suggest(q, 8);
            let people = [];
            try { people = await catalog.people(q, { limit: 3 }); } catch (e) { /* sin personas */ }
            if (q !== input.value.trim()) return; // ya se ha escrito otra cosa
            if (!items.length && !people.length) return hideSuggest();
            const itemHtml = items.map((s) => {
                const action = s.type === 'history' ? `data-hq="${escapeHtml(s.text)}"` : s.type === 'live' ? `data-play-live="${escapeHtml(s.id)}" data-hq-save="${escapeHtml(s.text)}"` : `data-open="${s.type}:${escapeHtml(s.id)}" data-hq-save="${escapeHtml(s.text)}"`;
                return `<button class="search-suggest-item" ${action}><span class="mi">${SUGGEST_ICON[s.type]}</span><span class="search-suggest-text">${escapeHtml(s.text)}</span>${s.type !== 'history' ? `<span class="search-suggest-kind">${t(s.type === 'vod' ? 'search.movies' : s.type === 'series' ? 'search.series' : 'search.channels')}</span>` : ''}</button>`;
            }).join('');
            const peopleHtml = people.map((p) => `<button class="search-suggest-item" data-actor="${escapeHtml(p.name)}" data-hq-save="${escapeHtml(q)}"><span class="mi">${p.roles.length === 1 && p.roles[0] === 'director' ? 'movie_creation' : 'person'}</span><span class="search-suggest-text">${escapeHtml(p.name)}</span><span class="search-suggest-kind">${t('search.people')}</span></button>`).join('');
            suggest.innerHTML = peopleHtml + itemHtml;
            suggest.hidden = false;
        };

        const saveQuery = (q) => {
            clearTimeout(saveTimer);
            searchHistory.add(q);
        };

        // «Personas»: actores y directores cuyo nombre coincide; al pulsar uno se ven sus películas y series.
        const paintPeople = (q, people, total) => {
            if (!people.length) return;
            const html = `<section class="section search-people"><div class="section-head"><h2 class="section-title">${t('search.people')}</h2></div>
                <div class="cast-list is-expanded" data-nav-row>${people.map((p) => `<button type="button" class="cast-chip" data-actor="${escapeHtml(p.name)}" data-hq-save="${escapeHtml(q)}"><span class="mi">${p.roles.length === 1 && p.roles[0] === 'director' ? 'movie_creation' : 'person'}</span>${escapeHtml(p.name)} · ${p.count}</button>`).join('')}</div></section>`;
            if (!total) {
                results.innerHTML = html + scanBarHtml();
                enhance(results);
                return;
            }
            const slot = results.querySelector('[data-people]');
            if (slot) { slot.innerHTML = html; enhance(slot); }
        };

        // Además de los repartos ya conocidos, TMDB sabe quién es la persona escrita y qué títulos suyos hay en el catálogo.
        // Se pregunta cuando el usuario se para a escribir (una consulta por pausa, cacheada).
        let remoteTimer = 0;
        const findRemotePerson = (q, people, total) => {
            clearTimeout(remoteTimer);
            if (!catalog.personSource || q.length < 4) return;
            remoteTimer = setTimeout(async () => {
                let found = null;
                try { found = await catalog.remotePerson(q); } catch (e) { /* sin TMDB */ }
                if (!found || !alive || q !== last) return;
                if (people.some((p) => normalizeText(p.name) === normalizeText(found.name))) return;
                paintPeople(q, [found].concat(people), total);
            }, 600);
        };

        const run = () => {
            const q = input.value.trim();
            clear.hidden = !q;
            if (q === last) return;
            last = q;
            clearTimeout(saveTimer);
            if (q.length < 2) {
                showRecent();
                return;
            }
            const res = catalog.search(q, { limit: 40 });
            const total = res.live.length + res.vod.length + res.series.length;
            catalog.people(q, { limit: 8 }).then((people) => {
                if (q !== last) return; // ya se ha escrito otra cosa
                paintPeople(q, people, total);
                findRemotePerson(q, people, total);
            }).catch(() => {});
            if (!total) {
                results.innerHTML = empty({ title: t('common.noResults'), text: t('common.noResultsHint') }) + scanBarHtml();
                return;
            }
            // Si el usuario se detiene en una búsqueda con resultados, se recuerda.
            saveTimer = setTimeout(() => searchHistory.add(q), 2500);
            results.innerHTML = `<div class="page-sub">${t('common.results', { count: total })}</div><div data-people></div>` +
                row({ id: 'live', title: t('search.channels'), items: res.live.map(channelCard), kind: 'channel' }) +
                (res.vod.length ? `<section class="section"><div class="section-head"><h2 class="section-title">${t('search.movies')}</h2></div><div class="grid">${res.vod.map(movieCard).join('')}</div></section>` : '') +
                (res.series.length ? `<section class="section"><div class="section-head"><h2 class="section-title">${t('search.series')}</h2></div><div class="grid">${res.series.map(seriesCard).join('')}</div></section>` : '') +
                scanBarHtml();
            enhance(results);
        };

        const setQuery = (q) => {
            input.value = q;
            hideSuggest();
            run();
            saveQuery(q);
            const first = (results.querySelector('.card') || results.querySelector('[data-open], [data-play-live]') || results.querySelector('a, button'));
            if (device.isTV && first) window.IPTV.tvnav.focus(first);
        };

        // Enlace de «compartir» (/search/?q=título): se abre con eso escrito y ya buscado.
        const preset = route && route.query && route.query.q ? String(route.query.q).slice(0, 120) : '';
        if (preset) { input.value = preset; setTimeout(() => { if (alive) run(); }, 60); }
        input.addEventListener('input', debounce(run, 200));
        input.addEventListener('input', debounce(renderSuggest, 120));
        input.addEventListener('focus', renderSuggest);
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                hideSuggest();
                run();
                if (input.value.trim().length >= 2) saveQuery(input.value.trim());
                input.blur();
                const first = (results.querySelector('.card') || results.querySelector('[data-open], [data-play-live]') || results.querySelector('a, button'));
                if (first) window.IPTV.tvnav.focus(first);
            } else if (e.key === 'ArrowDown' && !suggest.hidden) {
                e.preventDefault();
                const first = suggest.querySelector('button');
                if (first) first.focus();
            } else if (e.key === 'Escape') {
                hideSuggest();
            }
        });
        suggest.addEventListener('keydown', (e) => {
            const items = Array.from(suggest.querySelectorAll('button'));
            const i = items.indexOf(document.activeElement);
            if (e.key === 'ArrowDown' && i < items.length - 1) { e.preventDefault(); items[i + 1].focus(); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); if (i > 0) items[i - 1].focus(); else input.focus(); }
            else if (e.key === 'Escape') { hideSuggest(); input.focus(); }
        });
        // Al salir del campo se ocultan las sugerencias (con margen para que el clic llegue).
        input.addEventListener('blur', () => setTimeout(() => { if (!suggest.contains(document.activeElement)) hideSuggest(); }, 150));

        root.addEventListener('click', (e) => {
            if (e.target.closest('[data-cast-scan]')) { e.preventDefault(); startScan(); return; }
            if (e.target.closest('[data-cast-stop]')) { e.preventDefault(); stopScan = true; return; }
            const hq = e.target.closest('[data-hq]');
            if (hq) { e.preventDefault(); setQuery(hq.getAttribute('data-hq')); return; }
            const hx = e.target.closest('[data-hx]');
            if (hx) { e.preventDefault(); searchHistory.remove(hx.getAttribute('data-hx')); showRecent(); return; }
            if (e.target.closest('[data-history-clear]')) { e.preventDefault(); searchHistory.clear(); showRecent(); return; }
            const save = e.target.closest('[data-hq-save]');
            if (save) { saveQuery(input.value.trim() || save.getAttribute('data-hq-save')); hideSuggest(); return; }
            // Abrir un resultado consolida la búsqueda en el historial.
            if (results.contains(e.target) && e.target.closest('a, button') && input.value.trim().length >= 2) saveQuery(input.value.trim());
        });
        clear.addEventListener('click', () => {
            input.value = '';
            hideSuggest();
            run();
            input.focus();
        });
        showRecent();
        setTimeout(() => {
            try { if (!device.isTV) input.focus(); } catch (e) { /* ignore */ }
        }, 300);
        return () => { alive = false; stopScan = true; clearTimeout(saveTimer); clearTimeout(remoteTimer); };
    }
};
